import { useEffect } from "react";

/**
 * Keep-screen-on hook using the Wake Lock API. Active while `enabled` is true
 * AND the document is visible. Releases automatically on unmount,
 * visibility change, or when `enabled` flips to false.
 *
 * Falls back silently on browsers without Wake Lock support (most desktops
 * are fine since they don't auto-sleep, and unsupported mobile browsers
 * just keep their default behavior).
 */
export default function useWakeLock(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;

    let lock = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        lock = await navigator.wakeLock.request("screen");
        // Some browsers auto-release on visibility loss — re-request when we
        // become visible again (handler below).
      } catch (e) {
        // Ignore: NotAllowedError happens in iframes / when battery saver is on.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !cancelled) acquire();
    };

    document.addEventListener("visibilitychange", onVisibility);
    acquire();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (lock) {
        try { lock.release(); } catch (e) { /* noop */ }
      }
    };
  }, [enabled]);
}
